import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import * as actions from '../actions/initiatives.action';
import { switchMap, map, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { InitiativesService } from '../../services';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { Go } from '../../../store';
import { Store } from '@ngrx/store';
import { getInitiativesPage, getInitiativesSortOrder } from '../selectors';
import { getGeoLocationPosition } from 'src/app/geo-location/store';

@Injectable()
export class InitiativesEffects {
  constructor(
    private actions$: Actions,
    private service: InitiativesService,
    private router: Router,
    private sb: MatSnackBar,
    private store: Store
  ) {}

  LoadInitiative$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadInitiative),
      switchMap(({ id }) =>
        this.service.getInitiative(id).pipe(
          map(initiative => actions.LoadInitiativeSuccess({ initiative })),
          catchError(error => of(actions.LoadInitiativeFail({ error })))
        )
      )
    );
  });

  LoadInitiatives$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadInitiatives),
      withLatestFrom(this.store.select(getInitiativesSortOrder), this.store.select(getGeoLocationPosition)),
      switchMap(([{ skip, take, theme }, order, pos]) =>
        this.service.getInitiatives(skip, take, theme, order, `${pos?.coords.latitude}`, `${pos?.coords.longitude}`).pipe(
          map(initiatives => actions.LoadInitiativesSuccess({ initiatives })),
          catchError(error => of(actions.LoadInitiativesFail({ error })))
        )
      )
    );
  });

  LoadMoreInitiatives$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadMoreInitiatives),
      withLatestFrom(
        this.store.select(getInitiativesPage),
        this.store.select(getInitiativesSortOrder),
        this.store.select(getGeoLocationPosition)
      ),
      switchMap(([{}, pagedata, order, pos]) =>
        this.service
          .getInitiatives(
            (pagedata.currentPage + 1) * pagedata.pageSize,
            pagedata.pageSize,
            pagedata.filters['theme'],
            order,
            `${pos?.coords.latitude}`,
            `${pos?.coords.longitude}`
          )
          .pipe(
            map(initiatives => actions.LoadMoreInitiativesSuccess({ initiatives })),
            catchError(error => of(actions.LoadInitiativesFail({ error })))
          )
      )
    );
  });

  CreateInitiative$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.CreateInitiative),
      switchMap(({ initiativeData }) =>
        this.service.createInitiative(initiativeData).pipe(
          map(initiative => actions.CreateInitiativeSuccess({ initiative })),
          catchError(error => of(actions.CreateInitiativeFail({ error })))
        )
      )
    );
  });

  UpdateInitiative$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.UpdateInitiative),
      switchMap(({ id, initiativeData }) =>
        this.service.updateInitiative(id, initiativeData).pipe(
          map(initiative => actions.UpdateInitiativeSuccess({ initiative })),
          catchError(error => of(actions.UpdateInitiativeFail({ error })))
        )
      )
    );
  });

  DeleteInitiative$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.DeleteInitiative),
      switchMap(({ id }) =>
        this.service.deleteInitiative(id).pipe(
          map(initiative => actions.DeleteInitiativeSuccess({ initiative })),
          catchError(error => of(actions.DeleteInitiativeFail({ error })))
        )
      )
    );
  });

  updateInitiativeSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.UpdateInitiativeSuccess),
      tap(({ initiative }) => {
        this.sb.open('Initiatief geüpdatet!', '');
      }),
      map(({ initiative }) => Go({ path: initiative.url }))
    );
  });

  createInitiativeSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.CreateInitiativeSuccess),
      tap(({ initiative }) => {
        this.sb.open('Initiatief aangemaakt!', '');
      }),
      map(({ initiative }) => Go({ path: initiative.url }))
    );
  });

  deleteInitiativeSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.DeleteInitiativeSuccess),
      tap(({ initiative }) => {
        this.sb.open('Initiatief verwijderd!', '');
      }),
      map(({ initiative }) => Go({ path: ['/', 'initiatieven'] }))
    );
  });

  LikeInitiative$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LikeInitiative),
      switchMap(({ id }) =>
        this.service.likeInitiative(id).pipe(
          map(initiative => actions.LikeInitiativeSuccess({ initiative })),
          catchError(error => of(actions.LikeInitiativeFail({ error })))
        )
      )
    );
  });

  LoadByIdFail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.LoadInitiativeFail),
      map(({ error }) => {
        return Go({ path: ['/' + error?.status ? error.status : '404'], extras: { skipLocationChange: true } });
      })
    );
  });
}
